// Slepen en resizen op de tijdlijn — één pointer-gebaar, drie soorten.
//
// Bewust géén dnd-kit op dit scherm. dnd-kit denkt in drop-targets (je zou zes lanes ×
// twintig dagen aan droppables nodig hebben) en kan sowieso niet resizen; dan heb je twee
// mechanismen met twee bronnen van waarheid voor dezelfde datums. Eén pointer-hook is
// kleiner en gedraagt zich bij slepen en resizen identiek. dnd-kit blijft staan waar het
// al werkt (TaskBoard, Vandaag, WeekPlan).
//
// Alles rekent in wérkdagen, niet in kalenderdagen: een blok op vrijdag een kolom naar
// rechts hoort op maandag te landen, niet op zaterdag.

import { useCallback, useEffect, useRef, useState } from 'react';

import { verschuifWerkdagen, werkdagenInBereik } from '../../utils/dagstartLanes';

/** Onder deze verplaatsing is het een klik, geen sleep. Een muis beweegt altijd iets. */
const KLIK_DREMPEL = 4;

const datumDeel = (waarde) => String(waarde || '').slice(0, 10);

/**
 * Hoe ver mag dit gebaar maximaal?
 *
 * Twee grenzen. Resizen mag een blok niet door zichzelf heen duwen. En een blok dat
 * volledig buiten het venster ligt (`anker`, zie hieronder) staat vastgepind op de rand:
 * dat kan niet verder naar buiten dan die rand, want daar houden de kolommen op.
 * Verslepen binnen het venster is verder vrij — je mag een taak overal heen zetten.
 */
export function klemDelta(taak, soort, delta, anker) {
  if (soort === 'verplaats') {
    if (!anker?.dagen?.length) return delta;
    const kolom = Math.min(Math.max(anker.kolom + delta, 0), anker.dagen.length - 1);
    return kolom - anker.kolom;
  }
  const start = datumDeel(taak.startDate);
  const eind = datumDeel(taak.endDate) || start;
  // Bij een taak van één werkdag is de ruimte 0: korter dan één dag bestaat niet.
  const ruimte = Math.max(0, werkdagenInBereik(start, eind).length - 1);
  const geklemd = soort === 'links' ? Math.min(delta, ruimte) : Math.max(delta, -ruimte);
  // `Math.max(-3, -0)` geeft -0 terug. Dat rekent hetzelfde als 0, maar het is een
  // andere waarde voor Object.is — normaliseren scheelt verwarring verderop.
  return geklemd === 0 ? 0 : geklemd;
}

/**
 * De nieuwe datums na een gebaar van `delta` werkdagen.
 *
 * Normaal rekenen we op de échte datums en niet op de zichtbare kolommen. Een taak die
 * vóór het venster begon wordt links afgeknipt getekend (zie `kolommenVoor`) maar staat
 * met zijn eind gewoon in beeld; zou je zijn nieuwe datums uit kolomindexen afleiden,
 * dan verloor die taak bij elke sleep stilletjes zijn werkelijke duur.
 *
 * Eén uitzondering: een blok dat **volledig** buiten het venster ligt (een achterstallige
 * taak van vorige maand) staat vastgepind op de rand-kolom. Daar is de getekende plek een
 * leugen, en dan klopt schuiven-vanaf-de-echte-datum niet met wat je ziet: je sleept naar
 * vrijdag 14 en de taak van 9 juli wordt 10 juli — een dag ernaast én nog steeds buiten
 * beeld, dus het blok lijkt niet te bewegen. Met een `anker` ({dagen, kolom}) landt de
 * taak daarom op de datum van de kolom waar je hem loslaat, met behoud van de duur.
 */
export function datumsNaGebaar(taak, soort, ruweDelta, anker) {
  const start = datumDeel(taak?.startDate);
  if (!start) return null;
  const eind = datumDeel(taak.endDate) || start;
  const delta = klemDelta(taak, soort, ruweDelta, anker);

  if (soort === 'links') return { startDate: verschuifWerkdagen(start, delta), endDate: eind };
  if (soort === 'rechts') return { startDate: start, endDate: verschuifWerkdagen(eind, delta) };

  if (anker?.dagen?.length) {
    const kolom = Math.min(Math.max(anker.kolom + delta, 0), anker.dagen.length - 1);
    const nieuweStart = anker.dagen[kolom];
    const duur = Math.max(1, werkdagenInBereik(start, eind).length);
    return { startDate: nieuweStart, endDate: verschuifWerkdagen(nieuweStart, duur - 1) };
  }

  return {
    startDate: verschuifWerkdagen(start, delta),
    endDate: verschuifWerkdagen(eind, delta),
  };
}

/**
 * @param onKlaar  ({ taak, soort, delta, startDate, endDate }) => void — alleen bij een
 *                 gebaar dat écht iets verandert. Een klik gaat naar `onKlik`.
 * @returns { gebaar, startGebaar } — `gebaar` is null als er niets loopt.
 */
export function useTimelineDrag({ onKlaar }) {
  // De ref is de waarheid tijdens het gebaar; de state bestaat alleen om te hertekenen.
  // Zonder die scheiding zouden de window-listeners bij elke muisbeweging opnieuw
  // geregistreerd worden en zou `pointerup` een verouderde delta lezen.
  const staat = useRef(null);
  const [gebaar, setGebaar] = useState(null);
  const klaarRef = useRef(onKlaar);
  useEffect(() => { klaarRef.current = onKlaar; }, [onKlaar]);

  // `naarDelta` (optioneel): dx in pixels → werkdagen, voor een raster waarin werkdagen niet
  // even ver uit elkaar liggen (weekendkolommen ertussen, Dagstart per persoon).
  const startGebaar = useCallback(({ event, taak, soort, kolomBreedte, onKlik, anker, naarDelta }) => {
    // Alleen linkermuisknop; rechtsklik hoort het contextmenu te geven.
    if (event.button !== 0 || !kolomBreedte || !datumDeel(taak?.startDate)) return;
    event.preventDefault();
    event.stopPropagation();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Geen capture is niet fataal: de window-listeners vangen het gebaar ook op.
    }

    staat.current = {
      taak, soort, kolomBreedte, onKlik, anker, naarDelta,
      startX: event.clientX,
      delta: 0,
      bewogen: false,
    };
    setGebaar({ id: taak.id, soort, delta: 0, ...datumsNaGebaar(taak, soort, 0, anker) });
  }, []);

  useEffect(() => {
    if (!gebaar) return undefined;

    const beweeg = (e) => {
      const s = staat.current;
      if (!s) return;
      const dx = e.clientX - s.startX;
      if (Math.abs(dx) > KLIK_DREMPEL) s.bewogen = true;
      const ruw = s.naarDelta ? s.naarDelta(dx) : Math.round(dx / s.kolomBreedte);
      const delta = klemDelta(s.taak, s.soort, ruw, s.anker);
      // Alt of Shift ingedrukt tijdens het slepen = kopie in plaats van verplaatsen
      // (Joris, 14 sep). De toets telt op het moment van loslaten; onderweg tonen we het.
      const kopie = s.soort === 'verplaats' && Boolean(e.altKey || e.shiftKey);
      if (delta === s.delta && kopie === s.kopie) return;
      s.delta = delta;
      s.kopie = kopie;
      setGebaar({
        id: s.taak.id, soort: s.soort, delta, kopie,
        ...datumsNaGebaar(s.taak, s.soort, delta, s.anker),
      });
    };

    const beeindig = (e) => {
      const s = staat.current;
      staat.current = null;
      setGebaar(null);
      if (!s) return;
      // Nauwelijks bewogen: dit was een klik op het blok, niet een sleep.
      if (!s.bewogen) { s.onKlik?.(); return; }
      const kopie = s.soort === 'verplaats' && Boolean(e?.altKey || e?.shiftKey || s.kopie);
      // Een kopie op dezelfde dag is ook een kopie; een verplaatsing zonder delta niet.
      if (!s.delta && !kopie) return;
      klaarRef.current?.({
        taak: s.taak, soort: s.soort, delta: s.delta, kopie,
        ...datumsNaGebaar(s.taak, s.soort, s.delta, s.anker),
      });
    };

    // Afbreken schrijft niets weg — geen halve verplaatsing omdat een browser-gebaar
    // ertussen kwam, en Escape is de ontsnapping als je halverwege van gedachten verandert.
    const afbreken = () => { staat.current = null; setGebaar(null); };
    const opToets = (e) => { if (e.key === 'Escape') afbreken(); };

    window.addEventListener('pointermove', beweeg);
    window.addEventListener('pointerup', beeindig);
    window.addEventListener('pointercancel', afbreken);
    window.addEventListener('keydown', opToets);
    return () => {
      window.removeEventListener('pointermove', beweeg);
      window.removeEventListener('pointerup', beeindig);
      window.removeEventListener('pointercancel', afbreken);
      window.removeEventListener('keydown', opToets);
    };
    // Alleen op-/afbouwen bij het begin en einde van een gebaar; de delta leeft in de ref.
  }, [Boolean(gebaar)]); // eslint-disable-line react-hooks/exhaustive-deps

  return { gebaar, startGebaar };
}

/** Hoe lang na de laatste toetsaanslag we pas schrijven. */
export const TOETS_DEBOUNCE = 400;
/** Eén stap, en de grote stap met Shift erbij. */
export const TOETS_STAP = { klein: 1, groot: 5 };

/**
 * Hetzelfde verzetten, maar met het toetsenbord.
 *
 * Bestaat om het schermdelen: tijdens een dagstart praat je terwijl je verplaatst, en dan
 * is precies-genoeg-slepen met een muis op een gedeeld scherm bewerkelijk. Pijltjes zijn
 * exact en je hoeft je cursor niet te laten zien.
 *
 * Vijf keer op een pijltje drukken is één verplaatsing, geen vijf: de aanslagen tellen op
 * in de voorvertoning en pas als je even stil bent gaat er één PATCH uit. Anders vuur je
 * vijf schrijfacties op Airtable af, dat tikt tegen de rate limit én je krijgt vijf
 * undo-meldingen voor wat in je hoofd één beweging was.
 */
export function useToetsVerzet({ onKlaar }) {
  const [stand, setStand] = useState(null);
  const staat = useRef(null);
  const timer = useRef(null);
  const klaarRef = useRef(onKlaar);
  useEffect(() => { klaarRef.current = onKlaar; }, [onKlaar]);

  const schrijf = useCallback((s) => {
    if (!s || !s.delta) return;
    klaarRef.current?.({
      taak: s.taak, soort: s.soort, delta: s.delta,
      ...datumsNaGebaar(s.taak, s.soort, s.delta, s.anker),
    });
  }, []);

  const spoel = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const s = staat.current;
    staat.current = null;
    setStand(null);
    schrijf(s);
  }, [schrijf]);

  /** Weggooien zonder te schrijven — Escape halverwege een reeks aanslagen. */
  const afbreken = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const bezig = Boolean(staat.current);
    staat.current = null;
    setStand(null);
    return bezig;
  }, []);

  const verschuif = useCallback((taak, soort, stap, anker) => {
    const lopend = staat.current;
    // Een ander blok of een andere rand: de vorige beweging is af en mag weg. Zou je
    // hem laten staan, dan schrijft de timer straks een taak weg die je allang losliet.
    if (lopend && (lopend.taak.id !== taak.id || lopend.soort !== soort)) {
      if (timer.current) clearTimeout(timer.current);
      schrijf(lopend);
      staat.current = null;
    }
    const basis = staat.current?.taak.id === taak.id && staat.current.soort === soort
      ? staat.current
      : { taak, soort, delta: 0 };
    // Klemmen tegen de échte datums van de taak, niet tegen de vorige stap: anders kun
    // je een blok van één dag met vijf losse aanslagen alsnog omklappen.
    const delta = klemDelta(taak, soort, basis.delta + stap, anker);
    staat.current = { taak, soort, delta, anker };
    setStand({ id: taak.id, soort, delta, ...datumsNaGebaar(taak, soort, delta, anker) });

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(spoel, TOETS_DEBOUNCE);
  }, [schrijf, spoel]);

  // Blijft er een beweging hangen omdat het scherm wegklapt (andere persoon, andere
  // week), dan is die verplaatsing wél bedoeld — dus alsnog wegschrijven.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (staat.current?.delta) {
      klaarRef.current?.({
        taak: staat.current.taak, soort: staat.current.soort, delta: staat.current.delta,
        ...datumsNaGebaar(
          staat.current.taak, staat.current.soort, staat.current.delta, staat.current.anker
        ),
      });
    }
  }, []);

  return { toetsStand: stand, verschuif, spoel, afbreken };
}
